import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type RouteLocationNormalized,
} from 'vue-router';
import { useAuthStore, landingForRole } from '@/stores/auth.store';
import type { UserRole } from '@/types/api';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresGuest?: boolean;
    roles?: UserRole[];
    layout?: 'auth' | 'app';
    title?: string;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => {
      const auth = useAuthStore();
      if (!auth.isAuthenticated || !auth.role) return '/login';
      return landingForRole(auth.role);
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { requiresGuest: true, layout: 'auth', title: 'auth.loginTitle' },
  },

  // Admin
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/pages/dashboard/DashboardPage.vue'),
    meta: { requiresAuth: true, roles: ['ADMIN'], layout: 'app', title: 'dashboard.title' },
  },
  {
    path: '/tickets',
    name: 'admin-tickets',
    component: () => import('@/pages/admin/AdminTicketsListPage.vue'),
    meta: { requiresAuth: true, roles: ['ADMIN'], layout: 'app', title: 'nav.tickets' },
  },
  {
    path: '/kanban',
    name: 'admin-kanban',
    component: () => import('@/pages/admin/AdminKanbanPage.vue'),
    meta: { requiresAuth: true, roles: ['ADMIN'], layout: 'app', title: 'nav.kanban' },
  },
  {
    path: '/reports',
    name: 'admin-reports',
    component: () => import('@/pages/admin/AdminReportsPage.vue'),
    meta: { requiresAuth: true, roles: ['ADMIN'], layout: 'app', title: 'nav.reports' },
  },
  {
    path: '/calendar',
    name: 'admin-calendar',
    component: () => import('@/pages/admin/AdminCalendarPage.vue'),
    meta: { requiresAuth: true, roles: ['ADMIN', 'SUPPORT'], layout: 'app', title: 'nav.calendar' },
  },
  {
    path: '/gantt',
    name: 'admin-gantt',
    component: () => import('@/pages/admin/AdminGanttPage.vue'),
    meta: { requiresAuth: true, roles: ['ADMIN'], layout: 'app', title: 'nav.gantt' },
  },
  {
    path: '/settings/clients',
    name: 'admin-clients',
    component: () => import('@/pages/admin/AdminClientsPage.vue'),
    meta: { requiresAuth: true, roles: ['ADMIN'], layout: 'app', title: 'nav.clients' },
  },
  {
    path: '/users',
    name: 'admin-users',
    component: () => import('@/pages/admin/AdminUsersPage.vue'),
    meta: { requiresAuth: true, roles: ['ADMIN'], layout: 'app', title: 'nav.users' },
  },
  {
    path: '/profiles',
    name: 'admin-profiles',
    component: () => import('@/pages/admin/AdminProfilesPage.vue'),
    meta: { requiresAuth: true, roles: ['ADMIN'], layout: 'app', title: 'nav.profiles' },
  },

  // Customer
  {
    path: '/my-tickets',
    name: 'my-tickets',
    component: () => import('@/pages/portal/PortalInboxPage.vue'),
    meta: { requiresAuth: true, roles: ['CUSTOMER'], layout: 'app', title: 'nav.myTickets' },
  },
  {
    path: '/my-tickets/new',
    name: 'my-tickets-new',
    component: () => import('@/pages/portal/PortalNewTicketPage.vue'),
    meta: { requiresAuth: true, roles: ['CUSTOMER'], layout: 'app', title: 'nav.newTicket' },
  },

  // Support
  {
    path: '/assigned',
    name: 'support-assigned',
    component: () => import('@/pages/support/SupportAssignedPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPPORT'],
      layout: 'app',
      title: 'nav.assignedTickets',
    },
  },

  // Shared authenticated routes
  {
    path: '/tickets/:id',
    name: 'ticket-detail',
    component: () => import('@/pages/shared/TicketDetailPage.vue'),
    meta: { requiresAuth: true, layout: 'app', title: 'tickets.ticketDetail' },
  },
  {
    path: '/security',
    name: 'security',
    component: () => import('@/pages/shared/SecurityPage.vue'),
    meta: { requiresAuth: true, layout: 'app', title: 'nav.security' },
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/pages/shared/NotificationsPage.vue'),
    meta: { requiresAuth: true, layout: 'app', title: 'notifications.title' },
  },

  // 404
  {
    path: '/:pathMatch(.*)*',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { layout: 'app', title: 'errors.notFound' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to: RouteLocationNormalized) => {
  const auth = useAuthStore();

  // First-load bootstrap: try to silently refresh the session.
  if (!auth.isAuthenticated && !auth.bootstrapping) {
    await auth.bootstrap();
  }

  if (to.meta.requiresGuest && auth.isAuthenticated && auth.role) {
    return { path: landingForRole(auth.role), replace: true };
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath }, replace: true };
  }

  if (to.meta.roles && auth.role && !to.meta.roles.includes(auth.role)) {
    return { path: landingForRole(auth.role), replace: true };
  }

  return true;
});
